import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Stethoscope, Loader2, AlertCircle, CheckCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    const [userEmail, setUserEmail] = useState('');

    useEffect(() => {
        verifyToken();
    }, [token]);

    const verifyToken = async () => {
        try {
            const response = await authAPI.verifyResetToken(token);
            setTokenValid(true);
            setUserEmail(response.data.email);
        } catch (err) {
            setError(err.response?.data?.detail || 'Invalid or expired reset link');
            setTokenValid(false);
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        try {
            await authAPI.resetPassword(token, password);
            setSuccess(true);
            toast.success('Password reset successfully!');
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to reset password. Please try again.';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
                    <p className="text-slate-600">Verifying reset link...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    <Stethoscope className="h-10 w-10 text-indigo-600" />
                    <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                        MedPrep NBME & CBSE
                    </span>
                </div>

                <Card className="border-slate-200 shadow-lg">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Manrope' }}>
                            {success ? 'Password Reset!' : tokenValid ? 'Set New Password' : 'Link Expired'}
                        </CardTitle>
                        <CardDescription className="text-center">
                            {success 
                                ? "Your password has been updated"
                                : tokenValid 
                                    ? `Enter a new password for ${userEmail}`
                                    : "This reset link is no longer valid"
                            }
                        </CardDescription>
                    </CardHeader>

                    {success ? (
                        <CardContent className="space-y-4">
                            <div className="flex flex-col items-center gap-4 py-4">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                                </div>
                                <p className="text-center text-slate-600">
                                    You can now sign in with your new password.
                                </p>
                                <p className="text-sm text-slate-500">
                                    Redirecting to login...
                                </p>
                            </div>
                            <Button asChild className="w-full">
                                <Link to="/login">Go to Login</Link>
                            </Button>
                        </CardContent>
                    ) : tokenValid ? (
                        <form onSubmit={handleSubmit}>
                            <CardContent className="space-y-4">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                        {error}
                                    </div>
                                )}
                                
                                <div className="space-y-2">
                                    <Label htmlFor="password">New Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={6}
                                            className="pl-10 pr-10"
                                            data-testid="reset-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                        <Input
                                            id="confirmPassword"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            minLength={6}
                                            className="pl-10"
                                            data-testid="reset-confirm-password-input"
                                        />
                                    </div>
                                </div>

                                <p className="text-xs text-slate-500">
                                    Password must be at least 6 characters long
                                </p>
                            </CardContent>
                            <CardFooter>
                                <Button 
                                    type="submit" 
                                    className="w-full" 
                                    disabled={loading}
                                    data-testid="reset-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Resetting...
                                        </>
                                    ) : (
                                        'Reset Password'
                                    )}
                                </Button>
                            </CardFooter>
                        </form>
                    ) : (
                        <CardContent className="space-y-4">
                            <div className="flex flex-col items-center gap-4 py-4">
                                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                                    <AlertCircle className="h-8 w-8 text-red-600" />
                                </div>
                                <p className="text-center text-slate-600">
                                    {error || "This password reset link has expired or is invalid."}
                                </p>
                            </div>
                            <Button asChild className="w-full">
                                <Link to="/forgot-password">Request New Reset Link</Link>
                            </Button>
                        </CardContent>
                    )}
                </Card>

                <p className="mt-6 text-center text-sm text-slate-500">
                    <Link to="/login" className="hover:text-indigo-600">
                        ← Back to login
                    </Link>
                </p>
            </div>
        </div>
    );
}
